import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError, type ArchivedDocument, type Company, type IntakeAddress, type IntakeSender } from '../api';
import type { Dictionary, Locale } from '../i18n';
import { date as written } from '../format';
import * as Icon from '../components/Icons';
import { DocumentPreview } from '../components/DocumentPreview';
import { PostingWizard } from '../components/PostingWizard';
import type { Panel } from '../components/Shell';
import { useCompanyDocuments } from '../notifications/useCompanyDocuments';

type Tab = 'all' | 'new' | 'inProgress' | 'booked' | 'rejected';

/**
 * Archiwum oryginałów (E5-S2, E5-S5).
 *
 * Przyjmujemy zdjęcia i PDF-y takie, jakie są — nie przycinamy ich i nie zmieniamy formatu.
 * Przy kontroli liczy się dokument, który dostał podatnik, a nie to, co nasz program uznał
 * za wystarczające.
 */
export function DocumentsPanel({ company, t, locale, onGo, documentState }: { company: Company; t: Dictionary; locale: Locale; onGo?: (panel: Panel) => void; documentState?: ReturnType<typeof useCompanyDocuments> }) {
  const [wizardFor, setWizardFor] = useState<ArchivedDocument | null>(null);
  const localDocuments = useCompanyDocuments(company.id, !documentState);
  const { documents, refresh: load, error: loadError } = documentState ?? localDocuments;
  const [busy, setBusy] = useState(false);
  const [actionError, setError] = useState<string | null>(null);
  const error = actionError || loadError;
  const [info, setInfo] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [tab, setTab] = useState<Tab>('all');
  const picker = useRef<HTMLInputElement>(null);

  const editable = !company.roles.every((role) => role === 'VIEWER' || role === 'BOARD_MEMBER');

  /** Dokument bierze się do pracy albo odrzuca z powodem — obie decyzje zostają przy pliku. */
  async function decide(document: ArchivedDocument, action: 'accept' | 'reject') {
    let reason: string | null = null;
    if (action === 'reject') {
      reason = window.prompt(t.documents.rejectPrompt);
      if (!reason || reason.trim() === '') return;
    }
    setBusy(true);
    setError(null);
    try {
      if (action === 'accept') await api.acceptDocument(company.id, document.id);
      else await api.rejectDocument(company.id, document.id, reason!.trim());
      await load();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Pliki wysyłamy jeden po drugim, a nie równolegle.
   *
   * Przy kilkunastu zdjęciach naraz równoległa wysyłka zapycha łącze i częściej kończy się
   * zerwaniem niż przyspieszeniem, a przy zerwaniu nie wiadomo, które doszły.
   */
  async function send(files: FileList | File[]) {
    setBusy(true);
    setError(null);
    setInfo(null);
    let added = 0;
    let known = 0;
    try {
      for (const file of Array.from(files)) {
        const document = await api.uploadDocument(company.id, file);
        if (document.alreadyExisted) known += 1;
        else added += 1;
      }
      // O powtórzeniu mówimy wprost: zdjęcie zrobione w biegu łatwo wysłać dwa razy
      // i człowiek ma wiedzieć, że nie zginęło, a nie że się zdublowało
      setInfo(known > 0 ? t.documents.uploadedWithKnown(added, known) : t.documents.uploaded(added));
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      // Even a partially successful batch changes the company badge and archive.
      await load();
      setBusy(false);
    }
  }

  /** Otwarcie oryginału: podpisany adres z magazynu, a gdy go nie ma — pobranie przez API. */
  async function open(document: ArchivedDocument) {
    setError(null);
    try {
      const link = await api.documentLink(company.id, document.id);
      if (link.url) {
        window.open(link.url, '_blank', 'noopener');
        return;
      }
      const file = await api.documentContent(company.id, document.id);
      const url = URL.createObjectURL(file.blob);
      window.open(url, '_blank', 'noopener');
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    }
  }

  async function remove(document: ArchivedDocument) {
    if (!window.confirm(t.documents.confirmDelete(document.fileName))) return;
    setBusy(true);
    setError(null);
    try {
      await api.deleteDocument(company.id, document.id);
      await load();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  const all = documents ?? [];
  const byStatus = (status: ArchivedDocument['status']) => all.filter((document) => document.status === status);
  const shown =
    tab === 'new' ? byStatus('NEW') : tab === 'inProgress' ? byStatus('IN_PROGRESS') : tab === 'booked' ? byStatus('BOOKED') : tab === 'rejected' ? byStatus('REJECTED') : all;
  const statusTone = (status: ArchivedDocument['status']) =>
    status === 'BOOKED' ? 'chip st-ok' : status === 'REJECTED' ? 'chip st-bad' : status === 'IN_PROGRESS' ? 'chip st-info' : 'chip st-warn';

  return (
    <>
      <div className="hd">
        <div>
          <h3>{t.documents.inboxTitle}</h3>
          <p>{t.documents.inboxLead}</p>
        </div>
        <div className="sp">
          {onGo && (
            <button type="button" className="btn sec" onClick={() => onGo('mailboxConfig')}>
              <Icon.Gear />
              {t.documents.mailboxConfig}
            </button>
          )}
          {editable && (
            <button type="button" className="btn" disabled={busy} onClick={() => picker.current?.click()}>
              <Icon.Plus />
              {t.documents.choose}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="alert a-bad">
          <Icon.Error />
          <div>
            <b>{error}</b>
          </div>
        </div>
      )}
      {info && (
        <div className="alert a-ok">
          <Icon.Check />
          <div>
            <b>{info}</b>
          </div>
        </div>
      )}

      {/*
       * Kreator zajmuje ekran sam, a obok niego stoi podgląd oryginału.
       *
       * Numer, daty i kwoty przepisuje się **z dokumentu**, więc musi być widoczny w tej samej
       * chwili co pole. Pod kreatorem leżała dotąd lista skrzynki z tym samym dokumentem, pole
       * do wgrywania kolejnych plików i zapowiedź odczytu ze skanu — przy przepisywaniu kwot
       * każda z tych rzeczy tylko odciąga wzrok. „Dokończę później" wraca do pełnej skrzynki.
       */}
      {wizardFor && (
        <div className="grid two">
          <PostingWizard
            company={company}
            document={wizardFor}
            t={t}
            locale={locale}
            onClose={() => {
              setWizardFor(null);
              void load();
            }}
            onFinished={(next) => {
              setWizardFor(null);
              void load();
              onGo?.(next);
            }}
          />
          <DocumentPreview company={company} document={wizardFor} t={t} />
        </div>
      )}

      {!wizardFor && (
        <>
        <div className="panel">
          <div className="tabs" role="tablist">
            {(
              [
                ['all', `${t.invoices.tabAll} · ${all.length}`],
                ['new', `${t.documents.statuses.NEW} · ${byStatus('NEW').length}`],
                ['inProgress', `${t.documents.statuses.IN_PROGRESS} · ${byStatus('IN_PROGRESS').length}`],
                ['booked', `${t.documents.statuses.BOOKED} · ${byStatus('BOOKED').length}`],
                ['rejected', `${t.documents.statuses.REJECTED} · ${byStatus('REJECTED').length}`],
              ] as [Tab, string][]
            ).map(([key, label]) => (
              <button type="button" role="tab" key={key} aria-selected={tab === key} onClick={() => setTab(key)}>
                {label}
              </button>
            ))}
          </div>

          {editable && (
            <div
              className={`drop${dragging ? ' over' : ''}`}
              onDragOver={(event) => {
                event.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(event) => {
                event.preventDefault();
                setDragging(false);
                if (event.dataTransfer.files.length > 0) void send(event.dataTransfer.files);
              }}
            >
              <span className="drop-icon"><Icon.Doc /></span>
              <div className="drop-copy">
                <b>{t.documents.drop}</b>
                <p className="sm muted">{t.documents.accepted}</p>
              </div>
              <button type="button" className="btn sm" disabled={busy} onClick={() => picker.current?.click()}>
                <Icon.Plus />
                {t.documents.choose}
              </button>
            </div>
          )}

          <input
            ref={picker}
            type="file"
            multiple
            accept="image/jpeg,image/png,image/heic,image/heif,application/pdf"
            hidden
            onChange={(event) => {
              if (event.target.files && event.target.files.length > 0) void send(event.target.files);
              // Bez tego wybranie tego samego pliku drugi raz nie wywoła zdarzenia
              event.target.value = '';
            }}
          />

          {shown.length === 0 ? (
            <div className="empty">
              <b>{t.documents.none}</b>
              <p>{t.documents.noneHint}</p>
            </div>
          ) : (
            <div className="tbl">
              <table>
                <thead>
                  <tr>
                    <th>{t.documents.fileName}</th>
                    <th>{t.documents.added}</th>
                    <th>{t.documents.source}</th>
                    <th>{t.documents.tag}</th>
                    <th className="n">{t.documents.size}</th>
                    <th>{t.documents.state}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {shown.map((document) => (
                    <tr key={document.id}>
                      <td>
                        <button type="button" className="btn ghost sm" onClick={() => void open(document)}>
                          {document.fileName}
                        </button>
                      </td>
                      <td className="doc">{written(document.createdAt, locale)}</td>
                      <td>
                        {document.source === 'EMAIL' ? t.documents.sourceEmail : t.documents.sourceUpload}
                        {document.receivedFrom && <div className="hint">{document.receivedFrom}</div>}
                      </td>
                      <td>{document.tag ? <span className="chip">{document.tag}</span> : ''}</td>
                      <td className="n">{formatSize(document.byteSize)}</td>
                      <td>
                        <span className={statusTone(document.status)}>
                          <i />
                          {t.documents.statuses[document.status]}
                        </span>
                        {document.status === 'REJECTED' && document.rejectionReason && <div className="hint">{document.rejectionReason}</div>}
                      </td>
                      <td className="act">
                        <div className="row">
                          {editable && (document.status === 'NEW' || document.status === 'IN_PROGRESS') && (
                            <button type="button" className="btn sm" disabled={busy} onClick={() => setWizardFor(document)}>
                              {t.wizard.open}
                            </button>
                          )}
                          {/* Czynności poza kreatorem idą ikonami: kolumna czynności jest przyklejona
                              do prawej krawędzi i przy czterech podpisach zasłaniała kolumnę rozmiaru
                              i stanu. Nazwa zostaje w `aria-label`, więc nic się nie gubi */}
                          {editable && document.status === 'NEW' && (
                            <button
                              type="button"
                              className="btn sec sm ico"
                              disabled={busy}
                              aria-label={t.documents.accept}
                              title={t.documents.accept}
                              onClick={() => void decide(document, 'accept')}
                            >
                              <Icon.Check />
                            </button>
                          )}
                          {editable && (document.status === 'NEW' || document.status === 'IN_PROGRESS') && (
                            <button
                              type="button"
                              className="btn ghost sm ico"
                              disabled={busy}
                              aria-label={t.documents.reject}
                              title={t.documents.reject}
                              onClick={() => void decide(document, 'reject')}
                            >
                              <Icon.Error />
                            </button>
                          )}
                          {editable && !document.costDocumentId && (
                            <button
                              type="button"
                              className="btn danger sm ico"
                              disabled={busy}
                              aria-label={t.documents.delete}
                              title={t.documents.delete}
                              onClick={() => void remove(document)}
                            >
                              <Icon.Trash />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Skan jest dziś wyłącznie załącznikiem — nie udajemy, że coś z niego odczytujemy */}
        <div className="panel future">
          <div className="hd2">
            <b>{t.costs.scanFutureTitle}</b>
            <span className="soon">{t.invoices.soon}</span>
          </div>
          <p className="sm muted">{t.documents.scanFutureText}</p>
        </div>
        </>
      )}
    </>
  );
}

/**
 * Adresy wpływu i zatwierdzeni nadawcy (E17-S2).
 *
 * Adresów może być kilka, każdy z etykietą, którą dostają załączniki; unieważnienie działa od
 * razu, a nowy adres to nowy sekret. Nadawcy: osoby z dostępem do firmy są zatwierdzone
 * automatycznie, resztę dopisuje właściciel, administrator albo księgowa.
 */
export function IntakePanel({ company, t, locale, manager }: { company: Company; t: Dictionary; locale: Locale; manager: boolean }) {
  const [addresses, setAddresses] = useState<IntakeAddress[] | null>(null);
  const [senders, setSenders] = useState<IntakeSender[]>([]);
  const [tag, setTag] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [loadedAddresses, loadedSenders] = await Promise.all([api.intakeAddresses(company.id), api.intakeSenders(company.id)]);
      setAddresses(loadedAddresses);
      setSenders(loadedSenders);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    }
  }, [company.id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  async function copy(address: string) {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(address);
    } catch {
      setCopied(null);
    }
  }

  return (
    <>
      <div className="hd">
        <h3>{t.documents.mailboxConfig}</h3>
      </div>
      <div className="panel">
      <div className="hd">
        <div>
          <h4>{t.documents.intakeTitle}</h4>
          <p>{t.documents.intakeLead}</p>
        </div>
      </div>
      {error && (
        <div className="alert a-bad">
          <Icon.Error />
          <div>
            <b>{error}</b>
          </div>
        </div>
      )}

      {manager && (
        <div className="row">
          <label className="fld" style={{ flex: 1, minWidth: 220 }}>
            <span>{t.documents.intakeTag}</span>
            <input className="inp" value={tag} placeholder={t.documents.intakeTagHint} onChange={(event) => setTag(event.target.value)} />
          </label>
          <div className="fld">
            <span>&nbsp;</span>
            <button
              type="button"
              className="btn"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  await api.createIntakeAddress(company.id, tag.trim() || null);
                  setTag('');
                })
              }
            >
              {t.documents.intakeCreate}
            </button>
          </div>
        </div>
      )}

      {addresses && addresses.length === 0 && <p className="muted">{t.documents.intakeNone}</p>}
      {addresses && addresses.length > 0 && (
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th>{t.documents.intakeAddress}</th>
                <th>{t.documents.tag}</th>
                <th>{t.documents.added}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {addresses.map((address) => (
                <tr key={address.id} className={address.active ? undefined : 'muted'}>
                  <td className="mono">
                    {address.active ? (
                      <button type="button" className="btn ghost sm" onClick={() => void copy(address.address)}>
                        {address.address}
                      </button>
                    ) : (
                      <s>{address.address}</s>
                    )}
                    {copied === address.address && <span className="hint"> {t.documents.intakeCopied}</span>}
                    {!address.active && <span className="chip st-bad">{t.documents.intakeRevoked}</span>}
                  </td>
                  <td>{address.tag ? <span className="chip">{address.tag}</span> : ''}</td>
                  <td>{written(address.createdAt, locale)}</td>
                  <td className="act">
                    {manager && address.active && (
                      <button
                        type="button"
                        className="btn danger sm"
                        disabled={busy}
                        onClick={() => {
                          if (window.confirm(t.documents.intakeRevokeConfirm(address.address))) void run(() => api.revokeIntakeAddress(company.id, address.id));
                        }}
                      >
                        {t.documents.intakeRevoke}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="hd">
        <div>
          <h4>{t.documents.sendersTitle}</h4>
          <p>{t.documents.sendersLead}</p>
        </div>
      </div>
      {manager && (
        <div className="row">
          <label className="fld" style={{ flex: 1, minWidth: 220 }}>
            <span>{t.documents.senderEmail}</span>
            <input className="inp" type="email" value={email} onChange={(event) => setEmail(event.target.value)} />
          </label>
          <div className="fld">
            <span>&nbsp;</span>
            <button
              type="button"
              className="btn sec"
              disabled={busy || email.trim() === ''}
              onClick={() =>
                void run(async () => {
                  await api.addIntakeSender(company.id, email.trim());
                  setEmail('');
                })
              }
            >
              {t.documents.senderAdd}
            </button>
          </div>
        </div>
      )}
      {senders.length === 0 ? (
        <p className="muted">{t.documents.sendersNone}</p>
      ) : (
        <ul className="plain">
          {senders.map((sender) => (
            <li key={sender.id}>
              <span className="mono">{sender.email}</span>{' '}
              {manager && (
                <button type="button" className="btn ghost sm" disabled={busy} onClick={() => void run(() => api.revokeIntakeSender(company.id, sender.id))}>
                  {t.documents.senderRevoke}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      </div>
    </>
  );
}

/** Rozmiar pliku po ludzku: kilobajty do megabajta, dalej megabajty z jednym miejscem. */
function formatSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
